// Two main flows surfaced here:
//
//   1. Generate media for an existing project:
//      POST /projects/{project_id}/media/generate   (no body)
//
//   2. Regenerate a storyboard:
//      POST /projects/{project_id}/storyboards/{settings_id}/regenerate
//
// Approve / generate-slides / generate-highlights live on adjacent paths;
// the same project_id + settings_id pair drives them.

import { useMemo, useState } from 'react';
import type { RequestSpec } from '@/types';
import type { PlaygroundProps } from './index';
import { Field, FormCard, PlaygroundPanel, Select, TextInput } from './_shared';

type Action =
  | 'generate'
  | 'regenerate-storyboard'
  | 'approve-storyboard'
  | 'generate-slides'
  | 'generate-highlights';

const ACTIONS: { id: Action; label: string; description: string }[] = [
  { id: 'generate', label: 'Generate media', description: 'POST /projects/{id}/media/generate · no body' },
  { id: 'regenerate-storyboard', label: 'Regenerate storyboard', description: 'POST /projects/{id}/storyboards/{settings_id}/regenerate' },
  { id: 'approve-storyboard', label: 'Approve storyboard', description: 'POST /projects/{id}/storyboards/{settings_id}/approve' },
  { id: 'generate-slides', label: 'Generate slides', description: 'POST /projects/{id}/storyboards/{settings_id}/slides' },
  { id: 'generate-highlights', label: 'Generate highlights', description: 'POST /projects/{id}/storyboards/{settings_id}/highlights' },
];

export function MediaPlayground({ mode }: PlaygroundProps) {
  const [action, setAction] = useState<Action>('generate');
  const [projectId, setProjectId] = useState('proj_xxx');
  const [settingsId, setSettingsId] = useState('');

  const spec: RequestSpec = useMemo(() => {
    const pid = projectId || '{project_id}';
    const sid = settingsId || '{settings_id}';
    switch (action) {
      case 'generate':
        return { method: 'POST', path: `/media/projects/${pid}/media/generate` };
      case 'regenerate-storyboard':
        return { method: 'POST', path: `/media/projects/${pid}/storyboards/${sid}/regenerate` };
      case 'approve-storyboard':
        return { method: 'POST', path: `/media/projects/${pid}/storyboards/${sid}/approve` };
      case 'generate-slides':
        return { method: 'POST', path: `/media/projects/${pid}/storyboards/${sid}/slides` };
      case 'generate-highlights':
        return { method: 'POST', path: `/media/projects/${pid}/storyboards/${sid}/highlights` };
    }
  }, [action, projectId, settingsId]);

  const needsSettingsId = action !== 'generate';

  return (
    <PlaygroundPanel
      title="Drive the media pipeline"
      description="Trigger the storyboard → slides → media generation pipeline for an existing project. Use the Project playground to create one, then come back here."
      spec={spec}
      mode={mode}
    >
      <FormCard>
        <Field label="Action">
          <Select value={action} onChange={(e) => setAction(e.target.value as Action)}>
            {ACTIONS.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label}
              </option>
            ))}
          </Select>
          <div className="lp-text-[11px] lp-text-ink-500 lp-font-mono lp-mt-1">
            {ACTIONS.find((a) => a.id === action)?.description}
          </div>
        </Field>
        <Field label="Project ID" hint="Required for every action">
          <TextInput value={projectId} onChange={(e) => setProjectId(e.target.value)} placeholder="proj_…" />
        </Field>
        {needsSettingsId && (
          <Field label="Settings ID" hint="The storyboard's own settings ID — found in the project's storyboard list">
            <TextInput value={settingsId} onChange={(e) => setSettingsId(e.target.value)} placeholder="ss_…" />
          </Field>
        )}
      </FormCard>
    </PlaygroundPanel>
  );
}
